import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { ShoppingCart, User, LogOut, LayoutDashboard, Menu, X, Package, Wrench, Image as ImageIcon, Trash2, FileText, Clock, FileDown } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OrdersList = () => {
  const [orders, setOrders] = useState([]);
  const { user } = useAuth();

  useEffect(() => {
    fetchOrders();
  }, [user]);

  const handlePrintInvoice = async (orderId) => {
    try {
      const response = await axios.get(`${API}/orders/${orderId}/invoice`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      const printWindow = window.open('', '_blank');
      printWindow.document.write(response.data);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => { printWindow.print(); }, 250);
    } catch (error) {
      toast.error('Failed to load invoice. Please try again.');
    }
  };

  const handleDownloadInvoice = async (orderId) => {
    try {
      const response = await axios.get(`${API}/orders/${orderId}/invoice`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      const element = document.createElement("a");
      const file = new Blob([response.data], {type: 'text/html'});
      element.href = URL.createObjectURL(file);
      element.download = `Invoice-${orderId}.html`;
      document.body.appendChild(element);
      element.click();
      document.body.removeChild(element);
    } catch (error) {
      toast.error('Failed to download invoice. Please try again.');
    }
  };

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders`);
      setOrders(response.data);
    } catch (error) {
      toast.error('Failed to fetch orders');
    }
  };

  const handleUpdateStatus = async (orderId, newStatus) => {
    try {
      await axios.put(`${API}/orders/${orderId}/status`, {
        status: newStatus
      });
      toast.success('Order status updated');
      fetchOrders();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (!window.confirm('Are you sure you want to delete this order?')) return;
    try {
      await axios.delete(`${API}/orders/${orderId}`);
      toast.success('Order deleted successfully');
      fetchOrders();
    } catch (error) {
      toast.error('Failed to delete order');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-500',
      confirmed: 'bg-green-500',
      processing: 'bg-blue-500',
      shipped: 'bg-purple-500',
      delivered: 'bg-green-600',
      cancelled: 'bg-red-500'
    };
    return colors[status] || 'bg-slate-500';
  };

  return (
    <div data-testid="orders-list-page">
      <h2 className="text-2xl font-heading font-bold uppercase mb-6">Orders</h2>
      <div className="space-y-4">
        {orders.length === 0 ? (
          <Card className="p-8 text-center border-slate-200">
            <p className="text-slate-600">No orders found</p>
          </Card>
        ) : (
          orders.map(order => (
            <Card key={order.id} className="p-6 border-slate-200" data-testid={`order-${order.id}`}>
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="font-semibold text-lg" data-testid={`order-id-${order.id}`}>{order.id}</h3>
                  <p className="text-sm text-slate-600">{order.user_name} • {order.user_email}</p>
                  <p className="text-sm text-slate-600">{order.mobile}</p>
                </div>
                <div className="text-right">
                  <div className="flex flex-col items-end gap-2">
                    <Badge className={`${getStatusColor(order.status)} text-white`} data-testid={`order-status-${order.id}`}>
                      {order.status}
                    </Badge>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="h-8 text-primary hover:bg-primary/5 border-primary/20"
                        onClick={() => handlePrintInvoice(order.id)}
                      >
                        <FileText className="h-4 w-4 mr-1.5" />
                        Print
                      </Button>
                        <Button 
                        variant="default" 
                        size="sm" 
                        className="h-8 bg-primary hover:bg-primary/90 text-white"
                        onClick={() => handleDownloadInvoice(order.id)}
                      >
                        <FileDown className="h-4 w-4 mr-1.5" />
                        Download
                      </Button>
                    </div>
                  </div>
                  <p className="text-sm text-slate-500 mt-2">
                    {new Date(order.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
              <div className="border-t pt-4">
                <p className="text-sm font-semibold mb-2">Items:</p>
                {order.items.map((item, idx) => (
                  <div key={idx} className="text-sm text-slate-600 mb-1">
                    {item.quantity}x <span className="font-medium text-slate-800">{item.product_name || 'Unknown Product'}</span> <span className="text-xs text-slate-400">({(item.product_id || '').substring(0, 8)}...)</span> @ ₹{item.price.toLocaleString()}
                  </div>
                ))}
                <div className="mt-4 flex justify-between items-center border-t pt-4">
                  <span className="font-semibold">Total Amount:</span>
                  <div className="text-right">
                    {order.discount > 0 && <p className="text-xs text-green-600 font-medium">Coupon Applied: -₹{order.discount.toLocaleString()}</p>}
                    <span className="text-xl font-bold text-primary" data-testid={`order-total-${order.id}`}>₹{(order.total_amount || 0).toLocaleString()}</span>
                  </div>
                </div>
              </div>
              <div className="mt-4 text-sm bg-slate-50 p-3 rounded">
                <p className="text-slate-600"><strong>Delivery Address:</strong> {order.delivery_address}</p>
              </div>

              {/* Order Timeline */}
              {order.timeline && order.timeline.length > 0 && (
                <div className="mt-6 pt-4 border-t">
                  <Label className="text-xs uppercase text-slate-500 mb-4 block flex items-center gap-1">
                    <Clock className="h-4 w-4" /> Order Timeline
                  </Label>
                  <div className="relative pl-4 space-y-4 before:absolute before:inset-0 before:ml-5 before:-translate-x-px before:w-0.5 before:bg-slate-200">
                    {order.timeline.map((event, idx) => (
                      <div key={idx} className="relative flex items-center gap-4">
                        <div className={`absolute left-0 w-2 h-2 rounded-full ring-4 ring-white shadow
                          ${idx === order.timeline.length - 1 ? 'bg-primary ring-primary/20' : 'bg-slate-300'}`} 
                        />
                        <div className="pl-6 w-full flex justify-between items-start">
                          <div>
                            <p className="text-sm font-semibold capitalize text-slate-800">{event.status}</p>
                            <p className="text-xs text-slate-500">{event.note || 'Status updated'}</p>
                          </div>
                          <p className="text-xs text-slate-400 whitespace-nowrap">
                            {new Date(event.timestamp).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {['admin', 'manager', 'staff'].includes(user?.role) && (
                <div className="mt-6 pt-4 border-t flex flex-col gap-2">
                  <div className="flex justify-between items-center mb-1">
                    <Label className="text-xs uppercase text-slate-500">Update Order Status</Label>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      onClick={() => handleDeleteOrder(order.id)}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </Button>
                  </div>
                  <div className="flex gap-2">
                    <select 
                      className="text-sm border border-slate-200 rounded-md p-2 flex-1"
                      value={order.status}
                      onChange={(e) => handleUpdateStatus(order.id, e.target.value)}
                      data-testid={`update-order-status-${order.id}`}
                    >
                      <option value="pending">Pending</option>
                      <option value="confirmed">Confirmed</option>
                      <option value="processing">Processing</option>
                      <option value="shipped">Shipped</option>
                      <option value="delivered">Delivered</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                    <Button variant="outline" size="sm" onClick={() => fetchOrders()}>Refresh</Button>
                  </div>
                </div>
              )}
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default OrdersList;
